import BarChartIcon from '@mui/icons-material/BarChart'
import BrokenImageOutlinedIcon from '@mui/icons-material/BrokenImageOutlined'
import EditIcon from '@mui/icons-material/Edit'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import {Box, CardActions, CardContent, CardHeader, Grid, Typography} from '@mui/material'
import {StrictMode, useCallback} from 'react'
import type {FC, ReactElement, ReactNode} from 'react'
import {useInView} from 'react-intersection-observer'
import {NavLink} from 'react-router-dom'
import ActionButton from './ActionButton.tsx'
import {useGetCockpitWarningsSignalBasedQuery} from '@apis/cockpit.ts'
import {filterWarnings} from '@app/util-functions.ts'
import CardWarning from '@containers/Dashboards/LinesOverview/shared/CardWarning.tsx'
import type {LineInterface} from '@interfaces/line'
import BasicCard from '@shared/components/BasicCard.tsx'
import LineStatusChip from '@shared/components/LineStatusChip.tsx'
import {useCustomTranslation} from '@shared/hooks/useCustomTranslation'
import {buildSignalExplorerUrl} from '@shared/utils/signalExplorerUrl'

const LineCard: FC<{
  line: LineInterface
  showDetails?: boolean
  disconnectedSignalsCount?: number
}> = ({line, showDetails = true, disconnectedSignalsCount = 0}): ReactElement => {
  const {ref, inView} = useInView({triggerOnce: true})
  const getClassName = useCallback(
    (image: string): string => (image ? 'valid-line-image' : 'no-valid-image'),
    [],
  )
  const {data: cockpitWarnings} = useGetCockpitWarningsSignalBasedQuery(
    {lineId: line.id as string},
    {skip: !inView},
  )
  const [t] = useCustomTranslation('lines_overview')
  const [tCockpit] = useCustomTranslation('cockpit')

  const getImageIcon = useCallback(
    (image: string): ReactNode | null =>
      !image ? (
        <Box display="flex" height="100%" justifyContent="center" alignItems="center">
          <BrokenImageOutlinedIcon
            sx={{
              height: '75px',
              width: '100px',
              color: 'divider',
            }}
            className="no-image-card"
          />
        </Box>
      ) : null,
    [],
  )

  const filteredWarnings = filterWarnings(cockpitWarnings || [])

  return (
    <StrictMode>
      <BasicCard
        ref={ref}
        key={line.id}
        id={line.id}
        data-name="line-card"
        data-testid="mbai-lines-overview-line-card-component"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        <CardHeader
          title={
            <NavLink to={`/line/${line.id}`} style={{textDecoration: 'none'}}>
              <Typography
                variant="h3"
                data-name="line-title"
                data-cy="mbai-lines-overview-line-card-title"
                sx={{
                  color: 'text.primary',
                  mb: 0,
                }}
              >
                {line.name}
              </Typography>
            </NavLink>
          }
          action={
            <Box sx={{display: 'flex'}}>
              {disconnectedSignalsCount > 0 ? (
                <NavLink to="/settings?sub_menu=machine_admin&tab=signals&connection_status=offline">
                  <Box sx={{paddingTop: '4px'}}>
                    <LineStatusChip
                      connectivityStatus="Disconnected"
                      bold
                      tooltipText={tCockpit('disconnectedSignalsTooltip', {
                        count: disconnectedSignalsCount,
                      })}
                      displayLabel={false}
                    />
                  </Box>
                </NavLink>
              ) : null}
              {filteredWarnings && filteredWarnings.length > 0 ? (
                <CardWarning warnings={filteredWarnings} lineId={line.id} />
              ) : null}
            </Box>
          }
          sx={{
            pb: 0,
            borderBottom: 'none !important',
          }}
        />
        <NavLink to={`/line/${line.id}`}>
          <Box
            className={line && line.image ? getClassName(line.image) : ''}
            sx={{
              height: '200px',
              mx: 2,
              mt: 2,
              backgroundColor: 'background.fadedOpaque',
            }}
            data-cy="mbai-line-card-image"
          >
            {line.image ? (
              <img
                src={line.image}
                loading="lazy"
                alt={line.name}
                style={{width: '100%', height: '100%', objectFit: 'cover'}}
              />
            ) : (
              getImageIcon(line.image)
            )}
          </Box>
        </NavLink>
        {showDetails ? (
          <CardContent
            sx={{
              '&:last-child': {pb: 3},
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box sx={{mb: line.description ? 2 : 0}}>
              {line.description ? (
                <Typography variant="body2" color="text.secondary">
                  {line.description}
                </Typography>
              ) : null}
            </Box>
            {line.production_area ? (
              <Box sx={{mb: '-3px'}}>
                <Typography variant="caption" color="text.disabled" component="span">
                  {t('production_area')}:
                </Typography>
                <Typography variant="caption" color="text.primary" component="span" sx={{ml: 0.5}}>
                  {line.production_area.name}
                </Typography>
              </Box>
            ) : null}
            {line.machine_type ? (
              <Box sx={{mb: '-3px'}}>
                <Typography variant="caption" color="text.disabled" component="span">
                  {t('line_type')}:
                </Typography>
                <Typography variant="caption" color="text.primary" component="span" sx={{ml: 0.5}}>
                  {line.machine_type.name}
                </Typography>
              </Box>
            ) : null}
          </CardContent>
        ) : null}
        <CardActions
          sx={{
            px: 2,
            pb: 2,
            pt: '6px !important',
            mt: 'auto',
            borderTop: 'none !important',
          }}
        >
          <Grid container spacing={1} sx={{flex: 1, minWidth: 0}}>
            <Grid size={4} sx={{minWidth: 0}}>
              <ActionButton
                title={t('view_line')}
                dataName="line-view-line-button"
                dataCy="mbai-lines-overview-line-card-view-line-button"
                link={`/line/${line.id}`}
                icon={<BarChartIcon />}
              />
            </Grid>
            <Grid size={4} sx={{minWidth: 0}}>
              <ActionButton
                title={t('view_signals')}
                dataName="line-view-signals-button"
                dataCy="mbai-lines-overview-line-card-view-signals-button"
                link={buildSignalExplorerUrl({lineId: line.id})}
                icon={<TrendingUpIcon />}
              />
            </Grid>
            <Grid size={4} sx={{minWidth: 0}}>
              <ActionButton
                title={t('edit_line')}
                dataCy="mbai-lines-overview-line-card-view-line-management-button"
                link={`/settings/?sub_menu=machine_admin&tab=line_management&id=${line.id}`}
                icon={<EditIcon />}
              />
            </Grid>
          </Grid>
        </CardActions>
      </BasicCard>
    </StrictMode>
  )
}

export default LineCard
